/**
 * VendorRegistrationService
 * Service for vendor registration operations (public registration flow)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for vendor registration operations
 *
 * Supports two flows:
 * 1. Anonymous registration (register): Creates user + vendor for new users
 * 2. Self-service registration (registerMy): Creates vendor for authenticated users
 *
 * Uses the /api/vendor-registration endpoint from VendorManagement API.
 */
export class VendorRegistrationService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorRegistrationService.DEFAULT_SERVICE_PATH,
            basePath: VendorRegistrationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Register a new vendor with user account
     * This endpoint is anonymous - no authentication required.
     * Creates: User account, Vendor, Contacts, Documents, Locations, and links them together.
     * The vendor will be in "Submitted" status pending admin approval.
     *
     * @param data - Complete registration data including user and vendor information
     * @returns Registration response with created IDs or error information
     */
    async register(data) {
        const response = await this.registerRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to register vendor');
        }
        return response.data;
    }
    /**
     * Register a new vendor with user account (with full response)
     * @param data - Complete registration data including user and vendor information
     * @returns ResponseData with registration response
     */
    registerRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    /**
     * Check if an email address is already registered.
     * This endpoint is anonymous.
     *
     * @param email - Email address to check
     * @returns True if email is already in use, false if available
     */
    async checkEmail(email) {
        const response = await this.checkEmailRaw(email);
        return response.data ?? false;
    }
    /**
     * Check if email is already in use with full response
     * @param email - Email address to check
     * @returns ResponseData with boolean indicating if email exists
     */
    checkEmailRaw(email) {
        const url = this.buildUrlWithParams('check-email', { email });
        return this.http.get(url);
    }
    /**
     * Check if a business name is already registered.
     * This endpoint is anonymous.
     *
     * @param businessName - Business name to check
     * @returns True if business name is already in use, false if available
     */
    async checkBusinessName(businessName) {
        const response = await this.checkBusinessNameRaw(businessName);
        return response.data ?? false;
    }
    /**
     * Check if business name is already in use with full response
     * @param businessName - Business name to check
     * @returns ResponseData with boolean indicating if business name exists
     */
    checkBusinessNameRaw(businessName) {
        const url = this.buildUrlWithParams('check-business-name', { businessName });
        return this.http.get(url);
    }
    /**
     * Check if email is available (inverse of checkEmail for convenience)
     * @param email - Email address to check
     * @returns True if email is available, false if already in use
     */
    async isEmailAvailable(email) {
        const exists = await this.checkEmail(email);
        return !exists;
    }
    /**
     * Check if business name is available (inverse of checkBusinessName for convenience)
     * @param businessName - Business name to check
     * @returns True if business name is available, false if already in use
     */
    async isBusinessNameAvailable(businessName) {
        const exists = await this.checkBusinessName(businessName);
        return !exists;
    }
    // ==================== SELF-SERVICE REGISTRATION (Authenticated Users) ====================
    /**
     * Register as a vendor for authenticated users
     * This endpoint REQUIRES authentication - userId is extracted from JWT token.
     * Creates: Vendor, links to existing user, and sets up initial data.
     * The vendor will be in "Draft" status for the user to complete their profile.
     *
     * @param data - Vendor registration data (no user account data needed)
     * @returns Registration response with created vendor ID or error information
     */
    async registerMy(data) {
        const response = await this.registerMyRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to register vendor');
        }
        return response.data;
    }
    /**
     * Register as a vendor for authenticated users (with full response)
     * @param data - Vendor registration data (no user account data needed)
     * @returns ResponseData with registration response
     */
    registerMyRaw(data) {
        const url = this.buildUrl('my');
        return this.http.post(url, data);
    }
}
VendorRegistrationService.DEFAULT_SERVICE_PATH = 'vendors';
VendorRegistrationService.BASE_PATH = 'api/vendor-registration';
//# sourceMappingURL=VendorRegistrationService.js.map